import { useState, type CSSProperties } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { CatchWeightLine } from '@popcart/core/v1';
import { api } from '../../api';
import { EmptyState, ErrorState, Loading, StatusPill, useTitle } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { Fig, PageHead, Stat } from '../../ui/viz';
import { STATE_ART } from '../../lib/voice.js';
import { count, peso } from '../../lib/format.js';
import { useOpsShops } from '../queries';
import { Modal, StaleNote } from '../payments/kit';
import { Dot, DriftBar, Hint } from '../payments/money';
import { PeriodPicker } from '../reports/PeriodPicker';
import { lastNDays, manilaStamp, type Period } from '../reports/period.js';

import { catchWeightKey, catchWeightParams, lineView, shopOptions, signedPct, sortByShop } from './lib.js';
import './catchweight.css';
import { Select } from '../../ui/controls';

/**
 * /ops/catch-weight: for a period (and one shop or all), how far weighed items drifted from their
 * estimate against the tolerance band, which hit the cap, and every weighed line with its photo.
 */
export function OpsCatchWeightPage() {
  useTitle('Catch-weight');
  const [period, setPeriod] = useState<Period>(() => lastNDays(7));
  const [shopId, setShopId] = useState<string | null>(null);
  const [photo, setPhoto] = useState<CatchWeightLine | null>(null);
  const shops = useOpsShops();
  const q = useQuery({
    queryKey: catchWeightKey(period, shopId),
    queryFn: () => api.opsCatchWeight(catchWeightParams(period, shopId)),
    placeholderData: keepPreviousData,
  });
  const data = q.data;
  const options = shopOptions(data, shops.data);
  const shopName = options.find((o) => o.id === shopId)?.name;
  const tol = data?.tolerancePct ?? 0;
  const lineDrift = (l: CatchWeightLine) => (l.estGrams > 0 ? ((l.actualGrams - l.estGrams) / l.estGrams) * 100 : null);
  // One scale for every bar on the page, so bars compare across rows.
  const scale = data ? Math.max(tol * 1.4, ...data.lines.map((l) => Math.abs(lineDrift(l) ?? 0)), ...data.byShop.map((s) => Math.abs(s.avgVariancePct))) : 1;

  return (
    <div className="mn-page">
      <PageHead
        title="Catch-weight"
        meta={
          <>
            {shopName ?? 'All shops'}
            {data ? <span className="dv-chip">±{data.tolerancePct}% tolerance</span> : null}
          </>
        }
      />

      <section className="card mn-bar dv-rise" aria-label="Filters" style={rise(0)}>
        <PeriodPicker
          idPrefix="cw-period"
          label="Period"
          value={period}
          onChange={setPeriod}
          presets={[
            { id: '7', label: 'Last 7 days', period: lastNDays(7) },
            { id: '30', label: 'Last 30 days', period: lastNDays(30) },
          ]}
        />
        <div className="field">
          <label htmlFor="cw-shop">Shop</label>
          <Select id="cw-shop" className="input" value={shopId ?? ''} onChange={(e) => setShopId(e.target.value || null)}>
            <option value="">All shops</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </div>
      </section>

      {q.isError && !data ? (
        <section className="card">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </section>
      ) : !data ? (
        <Loading label="Loading catch-weight" rows={4} />
      ) : (
        <div className="mn-page" aria-busy={q.isFetching || undefined}>
          <StaleNote show={q.isError} what="catch-weight" />
          <section aria-label="Summary" className="mn-stats5 dv-rise" style={rise(1)}>
            <Stat
              label="Weighed lines"
              value={count(data.summary.lines)}
              tone={data.summary.cappedLines ? 'alert' : undefined}
              sub={data.summary.cappedLines ? <span className="dv-chip bad">{count(data.summary.cappedLines)} over tolerance</span> : `${count(data.summary.cappedLines)} over tolerance`}
            />
            <Stat label="Average drift" value={signedPct(data.summary.avgVariancePct)} sub={<DriftBar pct={data.summary.avgVariancePct} tol={tol} scale={scale} />} />
            <Stat label="Shops absorbed" value={peso(data.summary.absorbedCentavos)} sub="Over the cap" />
            <Stat label="Refunded" value={peso(data.summary.refundedCentavos)} sub="Lighter, e-wallet" />
            <Stat label="COD adjusted" value={peso(data.summary.codAdjustedCentavos)} sub="Cash to collect" />
          </section>

          {data.summary.lines === 0 ? (
            <section className="card">
              <EmptyState art={STATE_ART.noResults} title="Nothing weighed in this period">
                Try a longer period, or all shops.
              </EmptyState>
            </section>
          ) : (
            <div className="mn-grid">
              <section className="card dv-card mn-12 dv-rise" aria-labelledby="cw-shop-h" style={rise(2)}>
                <div className="dv-card-h">
                  <h2 id="cw-shop-h">By shop</h2>
                  <span className="grow" />
                  <Hint text={`Average drift per shop. The shaded band is the ±${tol}% tolerance; past it the shop absorbs the rest.`} />
                </div>
                <ul className="mn-shares cols">
                  {sortByShop(data.byShop).map((s) => (
                    <li key={s.shopId} className="mn-share">
                      <span className="nm">
                        <Dot tone={Math.abs(s.avgVariancePct) > tol ? 'warn' : 'ok'} />
                        <button type="button" className="cw-link" onClick={() => setShopId(s.shopId)} aria-label={`Show only ${s.shopName}`}>
                          {s.shopName}
                        </button>
                      </span>
                      <span className="val">
                        <span className="mn-cap">
                          {count(s.lines)} {s.lines === 1 ? 'line' : 'lines'}
                        </span>
                        {s.cappedLines ? <StatusPill tone="warning">{count(s.cappedLines)} capped</StatusPill> : null}
                        <Fig text={signedPct(s.avgVariancePct)} size="xs" animate={false} />
                      </span>
                      <span className="bar-slot">
                        <DriftBar pct={s.avgVariancePct} tol={tol} scale={scale} label={`${s.shopName}: ${signedPct(s.avgVariancePct)} average drift`} />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="card dv-card mn-12 dv-rise" aria-labelledby="cw-lines-h" style={rise(3)}>
                <div className="dv-card-h">
                  <h2 id="cw-lines-h">Weighed lines</h2>
                  <span className="dv-chip">{count(data.lines.length)}</span>
                  <span className="grow" />
                  <span className="dv-sub">{data.lines.length >= 200 ? 'Latest 200' : 'Newest first'}</span>
                </div>
                <div className="table-wrap mn-table">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="sr-only">Photo</span>
                        </th>
                        <th scope="col">Item</th>
                        <th scope="col" className="num">
                          Weight
                        </th>
                        <th scope="col" style={{ width: 140 }}>
                          Drift
                        </th>
                        <th scope="col" className="num">
                          Price
                        </th>
                        <th scope="col" className="num">
                          Change
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lines.map((l, i) => {
                        const v = lineView(l);
                        return (
                          <tr key={`${l.orderId}-${i}`} className={l.capped ? 'ops-flag' : undefined}>
                            <td>
                              {l.photoUrl ? (
                                <button type="button" className="cw-thumb-btn" onClick={() => setPhoto(l)} aria-label={`Scale photo of ${l.productName}, ${l.code}`}>
                                  <img className="cw-thumb" src={l.photoUrl} alt="" loading="lazy" />
                                </button>
                              ) : (
                                <span className="mn-nophoto" title="No photo">
                                  <Icon name="camera" />
                                  <span className="sr-only">No photo</span>
                                </span>
                              )}
                            </td>
                            <td className="ops-wrap">
                              <div className="ops-cell-title">{l.productName}</div>
                              <div className="mn-cap">
                                <span className="mn-code">{l.code}</span> · {manilaStamp(l.weighedAt)} · {l.shopName}
                              </div>
                            </td>
                            <td className="num">
                              <span className="mn-cap">{v.est}</span>
                              <div className="ops-cell-title">{v.actual}</div>
                            </td>
                            <td>
                              <div className="stack s4">
                                <DriftBar pct={lineDrift(l)} tol={tol} scale={scale} />
                                <span className={l.capped ? 'mn-cap bad' : 'mn-cap'}>{v.variance}</span>
                              </div>
                            </td>
                            <td className="num">
                              <Fig text={v.finalPrice} size="xs" animate={false} />
                              <div className="mn-cap">est {v.estPrice}</div>
                            </td>
                            <td className="num">
                              <StatusPill tone={v.tone}>{v.delta}</StatusPill>
                              {v.capped ? <div className="mn-cap">Capped</div> : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
        </div>
      )}

      <Modal open={photo !== null} labelledBy="cw-photo-title" onClose={() => setPhoto(null)} wide>
        {photo ? (
          <div className="stack s24">
            <div className="stack s4">
              <h2 id="cw-photo-title" className="t-headline">
                {photo.productName} · {photo.code}
              </h2>
              <span className="t-caption muted">
                {photo.shopName} · {manilaStamp(photo.weighedAt)} · {lineView(photo).actual} for an estimated {lineView(photo).est}
              </span>
            </div>
            {photo.photoUrl ? <img className="cw-photo" src={photo.photoUrl} alt={`The scale reading ${lineView(photo).actual}`} /> : null}
            <div className="row ops-dialog-actions">
              <button type="button" className="btn btn-primary" onClick={() => setPhoto(null)} autoFocus>
                Done
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;
